import React, { useEffect, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { ContentWrapper, DocumentHeaderWrapper, DocumentWrapper, Selects } from './styles'
import { FiArrowLeft, FiArrowRight, FiMinusCircle, FiPlusCircle, FiRotateCw } from 'react-icons/fi'
import TextDropdown from 'components/TextDropdown'
import TooltipWrapper from 'pages/MyQuestionBooks/components/TooltipWrapper'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

interface ContentLessonPros {
  available?: any
  category?: any
  contents?: Array<any>
}

const DocumentLesson: React.FC<ContentLessonPros> = ({ contents, category, available }) => {
  const [numPages, setNumPages] = useState<number>()
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [scale, setScale] = useState<number>(0.8)
  const [rotate, setRotate] = useState<number>(0)
  const items = contents
    ?.filter((c: any) => c.category === category)
    .map((t) => ({
      label: t.name,
      media: t.media,
      value: t._id,
    }))
  const [item, setItem] = useState(items[0])

  useEffect(() => {
    if (category) setItem(items[0])
  }, [category])

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setPageNumber(1)
  }

  const pages = new Array(numPages).fill(1).map((_, index) => ({
    label: ++index,
    value: index,
  }))

  const handleRotate = (add: boolean) => {
    let value = 0

    if (add && rotate === 270) {
      value = 0
    } else if (!add && rotate === 0) {
      value = 270
    } else {
      value = add ? rotate + 90 : rotate - 90
    }

    setRotate(value)
  }

  const handleLeftButton = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault()
    setPageNumber(pageNumber - 1)
    return false
  }

  const handleRightButton = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault()
    setPageNumber(pageNumber + 1)
    return false
  }

  const handleZoomOut = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault()
    setScale(scale - 0.1)
    return false
  }

  const handleZoomIn = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault()
    setScale(scale + 0.1)
    return false
  }

  const handleScale = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault()
    handleRotate(true)
  }

  const disabledLeft = !pageNumber || pageNumber === 1
  const disabledRight = !numPages || pageNumber >= numPages
  const disabledZoomOut = scale <= 0.3
  const disabledZoomIn = scale >= 1.8

  const handleKeyPress = (event: any) => {
    const letter = event.key
    if (window.innerWidth >= 1080) {
      if (letter === 'ArrowRight' || event.keyCode === 102) {
        if (!disabledRight) {
          setPageNumber(pageNumber + 1)
        }
      } else if (letter === 'ArrowLeft' || event.keyCode === 100) {
        if (!disabledLeft) {
          setPageNumber(pageNumber - 1)
        }
      } else if (letter === 'R' || letter === 'r') {
        handleRotate(true)
      }
    }
  }

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress)
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [pageNumber])

  return !available ? (
    <div />
  ) : (
    <ContentWrapper preference="document">
      <DocumentHeaderWrapper>
        <div>
          <Selects>
            <TextDropdown
              options={items}
              value={item?.value || items[0]}
              onChange={(value) => setItem(items.find((i) => i.value === value))}
              customLabel={(option) => `${option?.label}`}
            />
          </Selects>
        </div>
        <div className="pdf-viewer-control">
          <div className="navigation">
            <div>
              <TooltipWrapper text="Página anterior" position="top">
                <button
                  disabled={disabledLeft}
                  onClick={handleLeftButton}
                  className={`arrow-button ${disabledLeft && 'disabled'}`}
                >
                  <FiArrowLeft className="icon" />
                </button>
              </TooltipWrapper>
            </div>
            <div className="mobile-hide">
              {numPages > 0 && (
                <Selects>
                  <TextDropdown
                    options={pages}
                    defaultValue={1}
                    value={pageNumber}
                    onChange={(value) => setPageNumber(value as number)}
                    customLabel={(option) => `${option?.value}`}
                  />
                </Selects>
              )}
            </div>
            <div className="mobile-show">{`${pageNumber} / ${numPages}`}</div>
            <div className="mobile-hide">{`/ ${numPages}`}</div>
            <div>
              <TooltipWrapper text="Próxima página" position="top">
                <button
                  disabled={disabledRight}
                  onClick={handleRightButton}
                  className={`arrow-button ${disabledRight && 'disabled'}`}
                >
                  <FiArrowRight className="icon" />
                </button>
              </TooltipWrapper>
            </div>
          </div>
          <div className="scale">
            <div>
              <TooltipWrapper text="Diminuir zoom" position="top">
                <button
                  disabled={disabledZoomOut}
                  onClick={handleZoomOut}
                  className={`arrow-button ${disabledZoomOut && 'disabled'}`}
                >
                  <FiMinusCircle className="icon" />
                </button>
              </TooltipWrapper>
            </div>

            <div className="current">{Math.round(scale * 100)}%</div>

            <div>
              <TooltipWrapper text="Aumentar zoom" position="top">
                <button
                  disabled={disabledZoomIn}
                  onClick={handleZoomIn}
                  className={`arrow-button ${disabledZoomIn && 'disabled'}`}
                >
                  <FiPlusCircle className="icon" />
                </button>
              </TooltipWrapper>
            </div>
          </div>

          <div className="rotate">
            <div>
              <TooltipWrapper text="Girar no sentido horário" position="top">
                <button onClick={handleScale} className="arrow-button">
                  <FiRotateCw className="icon" />
                </button>
              </TooltipWrapper>
            </div>
          </div>
        </div>
      </DocumentHeaderWrapper>
      <DocumentWrapper>
        <section>
          <Document file={item?.media?.url} onLoadSuccess={onDocumentLoadSuccess}>
            <Page
              rotate={rotate}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              pageNumber={pageNumber}
              scale={scale}
            />
          </Document>
        </section>
      </DocumentWrapper>
    </ContentWrapper>
  )
}

export default DocumentLesson
